import { useEffect, useRef, useState } from 'react';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import type { EventPropertiesMap } from 'contexts/analytics/types';

type ImpressionOptions = {
  threshold?: number;
  minVisibleMs?: number;
};

/**
 * Fires `eventName` once per `viewKey` when the element has been at least
 * `threshold` visible for `minVisibleMs`. Returns a ref callback.
 */
export const useTrackImpression = <T extends keyof EventPropertiesMap>(
  eventName: T,
  viewKey: string | undefined,
  properties?: EventPropertiesMap[T],
  { threshold = 0.5, minVisibleMs = 1000 }: ImpressionOptions = {},
) => {
  const { trackEvent } = useAnalytics();
  const [element, setElement] = useState<Element | null>(null);
  const trackedKeyRef = useRef<string>();
  const trackEventRef = useRef(trackEvent);
  trackEventRef.current = trackEvent;
  const propertiesRef = useRef(properties);
  propertiesRef.current = properties;

  useEffect(() => {
    if (!element || !viewKey || trackedKeyRef.current === viewKey) return;
    if (typeof IntersectionObserver === 'undefined') return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // The first callback reports the current state even below the threshold.
        if (entry.isIntersecting && entry.intersectionRatio >= threshold) {
          timer ??= setTimeout(() => {
            trackedKeyRef.current = viewKey;
            observer.disconnect();
            trackEventRef.current(eventName, propertiesRef.current);
          }, minVisibleMs);
        } else {
          clearTimeout(timer);
          timer = undefined;
        }
      },
      { threshold },
    );
    observer.observe(element);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [element, viewKey, eventName, threshold, minVisibleMs]);

  return setElement;
};
